// scheduling/components/JobProgressBar.jsx
// Reusable per-job progress bar: WIP logged (actuals) + future-scheduled (today
// onward) vs budget. Modeled on the My Backlog bar (StaffDashboard); used in the
// Jobs-view detail panel (SchedulerGrid) and the ClientView per-job expansion.
// Self-contained tooltip state so multiple instances coexist on one page.
//
// Pass `myWip`/`othersWip` for the My-Backlog "you vs others" split, or just
// `wipTotal` when there is no "me" context (Jobs view / ClientView).
import { useState } from 'react';
import { BRAND } from '../utils/schedConstants';

export default function JobProgressBar({
  budget = 0,
  myWip = 0,
  othersWip = 0,
  wipTotal,            // optional: total WIP (when there's no my/others split)
  scheduledTotal = 0,  // future-scheduled only (today onward)
  remaining = 0,
  overBudget = false,
  overage = 0,
  pctComplete = 0,
  wipByStaff = [],
  scheduledByStaff = [],
  placeholderHours = 0,  // of scheduledTotal, the part held by (dept, level) placeholders
  showHeader = true,
  showLegend = true,
  tipDirection = 'up',   // 'up' | 'down' — which way the by-staff popovers open
  pctAfter = null,       // optional projected % after a proposed edit → header shows "X% → Y% after this entry"
  ghostHours = 0,        // optional proposed-addition hours → hatched "ghost" segment (live preview)
}) {
  const [open, setOpen] = useState(null); // 'my' | 'others' | 'sched' | null
  const hasMine = myWip > 0;
  const totalWip = wipTotal != null ? wipTotal : (myWip + othersWip);
  const pct = (v) => (budget > 0 ? Math.round((v / budget) * 100) : 0);
  const myPct = Math.min(100, pct(myWip));
  const othersPct = Math.min(100 - myPct, pct(hasMine ? othersWip : totalWip));
  const schedPct = Math.min(Math.max(0, 100 - myPct - othersPct), pct(scheduledTotal));
  const ghostPct = ghostHours > 0 ? Math.max(0, Math.min(100 - myPct - othersPct - schedPct, pct(ghostHours))) : 0;
  const blueLabel = hasMine ? "Others' WIP" : 'WIP logged';

  const toggle = (seg, e) => { e.stopPropagation(); setOpen(prev => (prev === seg ? null : seg)); };
  const segStyle = (bg, seg) => ({
    background: bg, cursor: 'pointer', position: 'relative',
    outline: open === seg ? '2px solid #fff' : 'none',
    boxShadow: open === seg ? `0 0 0 3px ${bg}` : 'none',
    zIndex: open === seg ? 2 : 1, borderRadius: open === seg ? 2 : 0,
  });
  const vEdge = tipDirection === 'down' ? 'top' : 'bottom';
  const tipStyle = (align) => ({
    position: 'absolute', [vEdge]: 18, [align]: 0, background: '#1F2937', color: '#fff',
    borderRadius: 8, padding: '10px 14px', fontSize: 11, whiteSpace: 'nowrap', zIndex: 100,
    boxShadow: '0 4px 16px rgba(0,0,0,0.25)', minWidth: 150, pointerEvents: 'auto',
  });

  return (
    <div style={{ margin: '6px 0' }} onClick={() => setOpen(null)}>
      {showHeader && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, gap: 8 }}>
          <span style={{ fontSize: 11, color: '#6B7280' }}>
            {hasMine ? `${myWip}h you + ${othersWip}h others logged` : `${totalWip}h logged`} | {scheduledTotal}h scheduled
            {overBudget
              ? <span style={{ color: '#DC2626', fontWeight: 600 }}> | +{overage}h over budget</span>
              : ` | ${remaining}h remaining`}
          </span>
          <span style={{ fontSize: 11, fontWeight: 700, color: (pctAfter != null ? pctAfter : pctComplete) > 100 ? '#DC2626' : '#374151' }}>
            {pctComplete}%{pctAfter != null && pctAfter !== pctComplete ? ` → ${pctAfter}% after this entry` : ''}
          </span>
        </div>
      )}
      <div style={{ position: 'relative' }}>
        <div style={{ display: 'flex', height: 12, background: '#E5E7EB', borderRadius: 6, overflow: 'visible' }}>
          {myPct > 0 && (
            <div style={{ width: `${myPct}%`, ...segStyle(BRAND.deepTeal, 'my') }} onClick={e => toggle('my', e)}>
              {open === 'my' && (
                <div style={tipStyle('left')}>
                  <div style={{ fontWeight: 700, marginBottom: 2, color: '#A7F3D0' }}>Your WIP</div>
                  <div>{myWip}h logged</div>
                </div>
              )}
            </div>
          )}
          {othersPct > 0 && (
            <div style={{ width: `${othersPct}%`, ...segStyle(BRAND.oceanBlue, 'others') }} onClick={e => toggle('others', e)}>
              {open === 'others' && (
                <div style={tipStyle('left')}>
                  <div style={{ fontWeight: 700, marginBottom: 4, color: '#93C5FD' }}>{blueLabel} ({hasMine ? othersWip : totalWip}h)</div>
                  {wipByStaff.map((s, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '1px 0' }}>
                      <span>{s.name}</span><span style={{ fontWeight: 600 }}>{Math.round((s.hours || 0) * 100) / 100}h</span>
                    </div>
                  ))}
                  {wipByStaff.length === 0 && <div style={{ color: '#9CA3AF', fontStyle: 'italic' }}>No WIP detail</div>}
                </div>
              )}
            </div>
          )}
          {schedPct > 0 && (
            <div style={{ width: `${schedPct}%`, ...segStyle(BRAND.sageGreen, 'sched') }} onClick={e => toggle('sched', e)}>
              {open === 'sched' && (
                <div style={tipStyle('right')}>
                  <div style={{ fontWeight: 700, marginBottom: 4, color: '#BBF7D0' }}>Scheduled, today on ({scheduledTotal}h)</div>
                  {/* Placeholder hours are part of scheduledTotal but belong to a
                      (dept, level) slot, not a person — listed first and styled apart
                      so they never read as a staffer named "Placeholder". */}
                  {placeholderHours > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '1px 0', color: '#FCD34D', fontStyle: 'italic' }}>
                      <span>Placeholder</span><span style={{ fontWeight: 600 }}>{Math.round(placeholderHours * 100) / 100}h</span>
                    </div>
                  )}
                  {scheduledByStaff.map((s, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '1px 0' }}>
                      <span>{s.name}</span><span style={{ fontWeight: 600 }}>{Math.round((s.hours || 0) * 100) / 100}h</span>
                    </div>
                  ))}
                  {scheduledByStaff.length === 0 && placeholderHours <= 0 && <div style={{ color: '#9CA3AF', fontStyle: 'italic' }}>No future entries</div>}
                </div>
              )}
            </div>
          )}
          {ghostPct > 0 && (
            <div title={`Proposed +${Math.round(ghostHours * 100) / 100}h this entry`}
              style={{ width: `${ghostPct}%`, background: 'repeating-linear-gradient(45deg, rgba(37,99,235,0.5), rgba(37,99,235,0.5) 4px, rgba(37,99,235,0.18) 4px, rgba(37,99,235,0.18) 8px)' }} />
          )}
        </div>
      </div>
      {showLegend && (
        <div style={{ display: 'flex', gap: 12, fontSize: 10, color: '#9CA3AF', marginTop: 5, flexWrap: 'wrap' }}>
          {hasMine && <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: BRAND.deepTeal }} /> My WIP</span>}
          <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: BRAND.oceanBlue }} /> {hasMine ? "Others' WIP" : 'WIP logged'}</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: BRAND.sageGreen }} /> Scheduled</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: '#E5E7EB' }} /> Remaining</span>
        </div>
      )}
    </div>
  );
}
